import { atom, atomFamily } from 'recoil';

import * as K from 'common/constants';
import { DashboardType, DashboardV2, Dashboard as RawDashboard } from 'common/graphqlTypes';
import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { Dashboard, DashboardTabName, FilterConfigType } from 'components/dashboards/types';
import { concatenatePrefilters, processConfig } from 'components/dashboards/dashboardData';
import { recoilSelectorFamily } from 'common/util/recoil-selector-family';
import { LRQRequest } from 'hooks/useLRQ/useLRQ.types';
import { recoilSelector } from 'common/util/recoil-selector';

/**
 * Raw unprocessed dashboard config from the server, or null for a dashboard config which has not been successfully fetched.
 */
export const rawDashboardState = atomFamily<RawDashboard & DashboardV2 | null, string>({
  key: K.RECOIL_ATOM_KEY.RAW_DASHBOARD,
  default: null,
});

/**
 * Resolved {@link Dashboard} object, after processing the {@link rawDashboardState} with the current dashboard param
 * values from the url.
 */
export const dashboardState = recoilSelectorFamily<Dashboard | null, string>({
  key: K.RECOIL_SELECTOR_KEY.DASHBOARD,
  get: (page) => ({ get }): Dashboard | null => {
    const rawDashboard = get(rawDashboardState(page));
    const dashboardParams = get(searchParamState(K.URL_PARAM.DASHBOARD_PARAMS));
    const dashboardFilters = get(searchParamState(K.URL_PARAM.DASHBOARD_FILTERS));
    const activeTab = get(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));
    const dashboard = processConfig(rawDashboard, dashboardParams, dashboardFilters, activeTab);
    return dashboard;
  },
});

export const dashboardTabsState = atom<string[]>({
  key: K.RECOIL_ATOM_KEY.DASHBOARD_TABS,
  default: [],
});

export const initialDashboardTabNamesState = recoilSelector<DashboardTabName[]>({
  key: K.RECOIL_SELECTOR_KEY.INITIAL_DASHBOARD_TAB_NAMES,
  get: ({ get }): any => {
    const currentDashboardTabNames = get(dashboardTabsState);
    const defaultTab: DashboardTabName = {
      id: 0,
      originalName: '',
      newName: '',
      tabToRemove: false,
    };
    if (currentDashboardTabNames.length > 0 ) {
      // populate existing tabs in edit tabs dialog
      const formattedTabs = currentDashboardTabNames.map((tab: string, idx: number) => ({newName: tab, id: idx, originalName: tab, tabToRemove: false}));
      return formattedTabs;
    } else {
      // show 2 empty tabs by default in add tabs dialog
      return ([{...defaultTab}, {...defaultTab, ...{ id: 1 }}]);
    }
  },
});

export const dashboardFacetValuesRequestsState = atom<LRQRequest[]>({
  key: K.RECOIL_ATOM_KEY.DASHBOARD_FACET_VALUES_REQUESTS,
  default: K.EMPTY_ARRAY,
});

export const rawDashboardFilterConfigState = recoilSelector<string | null>({
  key: K.RECOIL_SELECTOR_KEY.RAW_DASHBOARD_FILTER_CONFIG,
  get: ({ get }): string => {
    const page = get(searchParamState(K.URL_PARAM.DASHBOARD_NAME));
    const rawDashboard = get(rawDashboardState(page));
    let dashboardConfig: any = rawDashboard;
    const activeTab = get(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));
    if (rawDashboard && rawDashboard?.configType === DashboardType.TABBED) {
      dashboardConfig = rawDashboard.tabs.find((tab: any) => tab.tabName === activeTab);
    } else if (rawDashboard?.configType === DashboardType.NOT_SPECIFIED) {
      dashboardConfig = rawDashboard.tabs[0];
    }
    return dashboardConfig?.filters ? dashboardConfig.filters : '';
  },
});

export const dashboardFilterConfigListState = recoilSelector<FilterConfigType[]>({
  key: K.RECOIL_SELECTOR_KEY.DASHBOARD_FILTER_CONFIG_LIST,
  get: ({ get }): FilterConfigType[] => {
    const rawDashboardFilterConfig = get(rawDashboardFilterConfigState);
    return rawDashboardFilterConfig ? JSON.parse(rawDashboardFilterConfig) : [];
  },
});

export const prefilterState = recoilSelector<string>({
  key: K.RECOIL_SELECTOR_KEY.PREFILTER,
  get: ({ get }): string => {
    const searchFilter = get(searchParamState(K.URL_PARAM.FILTER));
    const urlDashboardFilterParams = get(searchParamState(K.URL_PARAM.DASHBOARD_FILTERS));
    const dashboardFilterConfigList = get(dashboardFilterConfigListState);
    const prefilter = concatenatePrefilters(
      urlDashboardFilterParams,
      dashboardFilterConfigList,
      searchFilter,
    );
    
    return prefilter || '';
  },
});
